import { useCallback, useEffect, useMemo, useState } from 'react';

import { describeActionScope, describeActionTarget } from '#lib/describeActionScope';

import { useTableSelection } from './useTableSelection';

/**
 * Gmail-style table selection: the header checkbox selects the visible page, a banner then offers
 * every row of the filtered result. "All in filter" holds no ids — bulk actions send
 * `{ selectAll: true, filters }` and the backend resolves the set with the list's own filter
 * semantics. It survives paging and sorting and ends when `resetKey` (filters, context) changes.
 *
 * @param {Array<Object>} rows - The visible page
 * @param {Object} params
 * @param {number} params.totalCount - Rows in the whole filtered result
 * @param {Object} params.filters - Active list filters (sent with selectAll)
 * @param {string} params.resetKey - Ends "all in filter" when it changes (filters + context ids)
 * @param {string|((row: Object) => string)} [params.rowId='id']
 * @param {{ singular: string, plural: string, gender?: 'f'|'m'|'n' }} params.entityLabel - German noun of a
 *   row, e.g. `{ singular: 'Firma', plural: 'Firmen', gender: 'f' }` (see describeActionTarget)
 * @returns {{
 *   selectedIds: string[], selectAllInFilter: boolean, hasSelection: boolean, count: number,
 *   clear: () => void, toScope: (idsKey?: string) => Object|null, tableSelection: Object,
 *   toActionScope: (idsKey?: string) => Object, actionCount: number, isExplicitSelection: boolean,
 *   actionTarget: string, actionScopeLabel: string, entityLabel: Object,
 * }} `toScope` builds the request-body scope of the selection (null without one). `toActionScope` /
 *   `actionCount` are what an action-row button runs on: the selection, or everything in the filter;
 *   `actionScopeLabel` names that for Table's `actions` slot, `actionTarget` for modal texts.
 *   `tableSelection` is passed to Table's `selection` prop (includes the banner).
 */
export function useScopedSelection(rows, { totalCount, filters, resetKey, rowId = 'id', entityLabel }) {
	const page = useTableSelection(rows, { rowId });
	const { selectedIds, clear: clearPage, toggleOne, toggleAll, allChecked, someChecked, isSelected } = page;
	const [selectAllInFilter, setSelectAllInFilter] = useState(false);

	useEffect(() => {
		setSelectAllInFilter(false);
	}, [resetKey]);

	const clear = useCallback(() => {
		clearPage();
		setSelectAllInFilter(false);
	}, [clearPage]);

	const handleToggleOne = useCallback(
		(id) => {
			setSelectAllInFilter(false);
			toggleOne(id);
		},
		[toggleOne],
	);

	const handleToggleAll = useCallback(() => {
		if (selectAllInFilter) clear();
		else toggleAll();
	}, [selectAllInFilter, clear, toggleAll]);

	const handleSelectAllInFilter = useCallback(() => {
		clearPage();
		setSelectAllInFilter(true);
	}, [clearPage]);

	const toScope = useCallback(
		(idsKey = 'ids') => {
			if (selectAllInFilter) return { selectAll: true, ...(Object.keys(filters).length > 0 ? { filters } : {}) };
			return selectedIds.length > 0 ? { [idsKey]: selectedIds } : null;
		},
		[selectAllInFilter, filters, selectedIds],
	);

	const isFiltered = Object.keys(filters).length > 0;

	const toActionScope = useCallback(
		(idsKey = 'ids') => toScope(idsKey) ?? { selectAll: true, ...(isFiltered ? { filters } : {}) },
		[toScope, isFiltered, filters],
	);
	const canSelectAllInFilter = !selectAllInFilter && allChecked && totalCount > rows.length;

	const tableSelection = useMemo(
		() => ({
			isSelected: (id) => selectAllInFilter || isSelected(id),
			onToggleOne: handleToggleOne,
			onToggleAll: handleToggleAll,
			allChecked: selectAllInFilter || allChecked,
			someChecked: !selectAllInFilter && someChecked,
			scopeBanner: {
				selectAllInFilter,
				canSelectAllInFilter,
				pageCount: rows.length,
				totalCount,
				isFiltered,
				entityLabelPlural: entityLabel.plural,
				onSelectAllInFilter: handleSelectAllInFilter,
				onClear: clear,
			},
		}),
		[
			selectAllInFilter,
			isSelected,
			handleToggleOne,
			handleToggleAll,
			allChecked,
			someChecked,
			canSelectAllInFilter,
			rows.length,
			totalCount,
			isFiltered,
			entityLabel.plural,
			handleSelectAllInFilter,
			clear,
		],
	);

	const isExplicitSelection = !selectAllInFilter && selectedIds.length > 0;
	const actionCount = isExplicitSelection ? selectedIds.length : totalCount;
	const actionTargetParams = { count: actionCount, isSelection: isExplicitSelection, isFiltered, ...entityLabel };

	return {
		selectedIds,
		selectAllInFilter,
		hasSelection: selectAllInFilter || selectedIds.length > 0,
		count: selectAllInFilter ? totalCount : selectedIds.length,
		clear,
		toScope,
		tableSelection,
		toActionScope,
		actionCount,
		isExplicitSelection,
		actionTarget: describeActionTarget(actionTargetParams),
		actionScopeLabel: describeActionScope(actionTargetParams),
		entityLabel,
	};
}
